import i18n from '@fintronners/react-language/src/Lang';
import { thousandFormatNumbroCurrency } from '@fintronners/react-utils/src/numberUtilsTSX';
import type { TransferValidationContext } from './types';

/**
 * Validates withdrawal amount against available balance and limits
 */
export function validateWithdrawalAmount(
  amount: number,
  context: TransferValidationContext,
): string | null {
  const { availableWithdrawalAmount, withdrawalMin, withdrawalMax } = context;

  if (amount < withdrawalMin) {
    return i18n.t('miscScreens.transfers.transferMinAmountError', {
      amount: thousandFormatNumbroCurrency(withdrawalMin),
    });
  }

  if (amount > withdrawalMax) {
    return i18n.t('miscScreens.withdrawals.withdrawalLimitError', {
      amount: thousandFormatNumbroCurrency(withdrawalMax),
    });
  }

  if (amount > availableWithdrawalAmount) {
    return i18n.t('miscScreens.withdrawals.amountOverAvailable');
  }

  return null;
}

/**
 * Validates IRA deposit amount against contribution limits
 */
export function validateIRADepositAmount(
  amount: number,
  context: TransferValidationContext,
): string | null {
  const { contributionLimitLeft } = context;

  if (contributionLimitLeft === undefined) return null;

  if (contributionLimitLeft === 0) {
    return i18n.t('miscScreens.deposits.contributionLimitReached');
  }

  if (amount > contributionLimitLeft) {
    return i18n.t('miscScreens.deposits.amountOverContributionLimit', {
      limit: thousandFormatNumbroCurrency(contributionLimitLeft, { mantissa: 0 }),
    });
  }

  return null;
}

/**
 * Validates regular (non-IRA) deposit amount against daily limits
 */
export function validateRegularDepositAmount(
  amount: number,
  context: TransferValidationContext,
): string | null {
  const { dailyDepositLimit } = context;

  if (amount > dailyDepositLimit) {
    return i18n.t('miscScreens.deposits.amountOverDailyLimit', {
      limit: thousandFormatNumbroCurrency(dailyDepositLimit, { mantissa: 0 }),
    });
  }

  return null;
}

/**
 * Validates deposit amount based on account type
 */
export function validateDepositAmount(
  amount: number,
  context: TransferValidationContext,
): string | null {
  const { depositMin, isIRAAccount } = context;

  if (amount < depositMin) {
    return i18n.t('miscScreens.transfers.transferMinAmountError', {
      amount: thousandFormatNumbroCurrency(depositMin),
    });
  }

  // IRA-specific validations
  if (isIRAAccount) {
    return validateIRADepositAmount(amount, context);
  }

  // Non-IRA validations
  return validateRegularDepositAmount(amount, context);
}
